/**
 * Privacy Policy v1.0 — Yan-Vada (යන්-වාද).
 *
 * Six-section policy. SL-law grounded. PDPA No. 9 of 2022 compliant.
 * Placeholder copy in Sri Lankan English register (restrained, direct).
 * [REVIEW] markers at sections requiring external counsel.
 *
 * Version: 1.0
 * Last updated: 23 April 2026
 * Status: Draft — pending Steering Review SR-1→SR-4 + Law Stress Test LST-1→LST-2
 */

export interface PrivacySection {
  id: string
  num: string
  title: string
  body: string[]
  shanakaReview?: boolean
}

const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: 'who',
    num: '1',
    title: 'Who we are.',
    body: [
      'Yan-Vada is a podcast produced by DGTL Foundry (Pvt) Ltd, registered in Sri Lanka. This page covers the marketing website at vada.yan.lk, sponsor pitch pages under that domain, and the member area for invited partners and stakeholders.',
      'If you need to reach someone real about your data or this policy, email hello@yan.lk. Riz reads it.',
      '[REVIEW] — Add company registration number once incorporated. Confirm "hello@yan.lk" as DPO contact is sufficient or if named individual required by PDPA §18.',
    ],
    shanakaReview: true,
  },
  {
    id: 'what',
    num: '2',
    title: 'What we collect, and why.',
    body: [
      'We collect only what we need to run the site and manage sponsor relationships:',
      'Your name, work email address, and company name — but only if you request access through the invite form or sign in to view a pitch. We use these to send invitations, know who is reading which proposal, and manage access.',
      'Standard server connection information — IP address (anonymised where possible), timestamp of visit, browser type, and pages requested. This is kept for 30 days for security purposes (detecting abuse) then automatically deleted.',
      'Nothing else. No tracking pixels. No advertising network cookies. No analytics software that profiles you across sites. No session recording tools. If we add any of these in future, we will update this section and email you if you have an account with us.',
      '[REVIEW] — Clarify whether device type / user agent string collection requires explicit consent under PDPA §12(1)(a). Consider anonymising IP at collection point.',
    ],
    shanakaReview: true,
  },
  {
    id: 'share',
    num: '3',
    title: 'Who we share it with.',
    body: [
      'As little as possible. Specifically:',
      'Our email delivery service — to send you sign-in links or invitation emails. They cannot use your email for their own purposes under their own terms of service.',
      'Our hosting provider (Cloudflare) — to serve this website to you. Their logs are governed by their own privacy policy; we do not have access to their server-level logs.',
      'That is it. We do not sell data. We do not share it with advertising networks. We do not share it with third-party analytics platforms.',
      'If a court or regulator in Sri Lanka asks us for information with proper legal authority, we will comply. We will notify you before responding unless legally barred from doing so.',
      '[REVIEW] — Cloudflare as processor: confirm standard DPA/SCA in place. Cross-border transfer risk assessment if CF processes data outside SL.',
    ],
    shanakaReview: false,
  },
  {
    id: 'rights',
    num: '4',
    title: 'Your rights.',
    body: [
      'Under Sri Lanka\'s Personal Data Protection Act No. 9 of 2022 (PDPA), you have the right to:',
      'Ask us what personal data we hold about you — we will respond within a reasonable time, usually a few days.',
      'Ask us to correct any inaccurate data — we will update it promptly.',
      'Ask us to delete your data — we will remove it from our systems within 7 days, except where we are legally required to keep it (see Section 5 below).',
      'Withdraw consent at any time — tell us and we will stop processing your data for the purpose you originally consented to.',
      'Lodge a complaint with the PDPA Commissioner if you believe we have violated your rights under the Act.',
      'If you are in the EU or UK, GDPR-equivalent rights also apply. Same mailbox, same response window.',
      '[REVIEW] — Confirm PDPA Commissioner complaint process reference is current. Verify deletion request 7-day window aligns with PDPA §18(2)(c) erasure timeline.',
    ],
    shanakaReview: true,
  },
  {
    id: 'keep',
    num: '5',
    title: 'How long we keep things.',
    body: [
      'Account data (name, email, company): until you delete your account or 6 months of account inactivity, whichever comes first.',
      'Server connection logs: 30 days, then automatically purged.',
      'Signed NDAs and sponsorship agreements: 7 years from the date of signing, consistent with commercial record-keeping standards in Sri Lanka.',
      'T&C acceptance record: 3 years from acceptance date.',
      'When retention periods expire, data is securely deleted and not recoverable.',
      '[REVIEW] — 7-year NDA retention: confirm alignment with SL Limitation Act 1871 for contract-related documents. PDPA does not specify retention periods for this category.',
    ],
    shanakaReview: true,
  },
  {
    id: 'cookies',
    num: '6',
    title: 'Cookies & local storage.',
    body: [
      'We use two types of browser storage:',
      'A session cookie (if you sign in) — keeps you signed in during your browser session. This is essential for the member area to function. It expires when you close the tab.',
      'A preference cookie — remembers whether you\'ve accepted the Terms of Use so we don\'t show the gate again on every visit. No personal data in this cookie.',
      'No third-party cookies. No advertising cookies. No analytics cookies. No social media tracking pixels.',
      'We do not use local storage for anything beyond authentication state and preference flags listed above.',
      '[REVIEW] — Confirm cookie consent mechanism satisfies PDPA §12(1)(a) explicit consent requirement, or whether T&C blanket acceptance covers cookie storage as "strictly necessary".',
    ],
    shanakaReview: true,
  },
  {
    id: 'children',
    num: '7',
    title: 'Children.',
    body: [
      'This website is not directed at children under 16 years of age. We do not knowingly collect personal data from children. If we become aware that we have collected data from a child without parental consent, we will delete it promptly.',
      'If you are under 16 and viewing this page as part of a school project or curiosity, please do not submit any forms or provide your email address without a parent or guardian\'s permission.',
    ],
    shanakaReview: false,
  },
  {
    id: 'international',
    num: '8',
    title: 'International visitors.',
    body: [
      'If you are accessing this site from outside Sri Lanka, your data may be transferred to and processed on servers outside your country of residence (currently: Cloudflare\'s global CDN network).',
      'By using this site, you consent to this cross-border transfer for the limited purposes described in this policy.',
      'We do not transfer personal data outside of Sri Lanka for any other purpose without your explicit consent.',
      'Sri Lanka citizens: your data stays in Sri Lanka unless you explicitly agree otherwise.',
      '[REVIEW] — Cross-border transfer clause adequacy for EU (GDPR Chapter V), UK (UK GDPR), and other jurisdictions with data localization requirements. May need country-specific variant text for international sponsors.',
    ],
    shanakaReview: true,
  },
]

/**
 * Render the full Privacy Policy as a React component.
 * Also exports raw data for standalone /privacy route.
 */
export function PrivacyPolicyNext() {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="legal-page">
      <div className="legal-page-header">
        <span className="legal-header-badge">
          <span className="legal-dot" /> Yan-Vada · legal
        </span>
        <span className="legal-eyebrow">Privacy Policy · v1.0</span>
        <h1 className="legal-page-title">What we collect, what we don&apos;t.</h1>
        <p className="legal-page-subtitle">
          Short version: we collect your name and email when you request access or sign in. We don&apos;t sell it
          and we don&apos;t feed it to ad networks. The rest is the long version.
        </p>
      </div>

      <div className="legal-placeholder">
        Placeholder · OpenCode / Vidura to finalise · [REVIEW]
      </div>

      <div className="legal-layout">
        <nav className="legal-toc">
          <span className="legal-toc-label">Contents</span>
          <ol className="legal-toc-list">
            {PRIVACY_SECTIONS.map(s => (
              <li key={s.id}>
                <button
                  className="legal-toc-link"
                  onClick={() => scrollToSection(`section-${s.id}`)}
                >
                  §{s.num} {s.title}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="legal-content">
          {PRIVACY_SECTIONS.map(s => (
            <section key={s.id} id={`section-${s.id}`} className="legal-section">
              <div className="legal-section-num">§{s.num}</div>
              <h2 className="legal-section-title">{s.title}</h2>
              {s.shanakaReview && (
                <span className="shanaka-marker">[REVIEW]</span>
              )}
              <div className="legal-section-body">
                {s.body.map((para, i) => (
                  <p key={i} className="legal-p">{para}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <footer className="legal-version-footer">
        Version 1.0 · Last updated 23 April 2026 ·{' '}
        <a href="mailto:hello@yan.lk" className="legal-link">hello@yan.lk</a>
      </footer>
    </div>
  )
}

export { PRIVACY_SECTIONS }
